import { useMemo, useEffect, useState } from 'react';
import { Truck, FileText, ChevronDown, ChevronRight, FileUp, Boxes } from 'lucide-react';
import { ASNScheduleDetail } from '../asnModalTypes';
import { ASNFormData, DeliveryLineOption, SourceType } from './types';
import { useFormatters } from '../../../contexts/PreferencesContext';
import PriorityBadge from '../../call-off/shared/PriorityBadge';
import { PRIORITY_EDGE_CLASS, priorityFirst } from '../../call-off/shared/linePriority';

interface SourceLinesStepProps {
  sourceType: SourceType;
  onSourceChange: (s: SourceType) => void;
  formData: ASNFormData;
  onToggleSchedule: (id: number) => void;
  onToggleDeliveryLine: (id: number) => void;
  onSelectAllInGroup: (lines: DeliveryLineOption[]) => void;
  onOpenExcelImport: () => void;
  schedules: ASNScheduleDetail[];
  deliveryLines: DeliveryLineOption[];
  loadingSchedules: boolean;
  loadingDeliveryLines: boolean;
  /**
   * CR-6038: when the wizard is opened via the standalone Import & Allocate
   * "Create new ASN" route, the delivery lines on screen are supplier-
   * imported (source_type='SupplierImport'). The tab label and the empty
   * grouping header switch to "Imported Delivery Lines" so the supplier
   * isn't told they're looking at call-off lines that don't exist.
   */
  deliveryLineSource?: 'CallOff' | 'SupplierImport';
  /** CR-6038: switch the delivery-lines tab between call-off and imported
   *  sources so imported-but-unallocated lines aren't stranded. */
  onDeliveryLineSourceChange?: (s: 'CallOff' | 'SupplierImport') => void;
}

export function SourceLinesStep({
  sourceType,
  onSourceChange,
  formData,
  onToggleSchedule,
  onToggleDeliveryLine,
  onSelectAllInGroup,
  onOpenExcelImport,
  schedules,
  deliveryLines,
  loadingSchedules,
  loadingDeliveryLines,
  deliveryLineSource = 'CallOff',
  onDeliveryLineSourceChange,
}: SourceLinesStepProps) {
  const { formatDate } = useFormatters();
  const isImported = deliveryLineSource === 'SupplierImport';
  const emptyGroupLabel = isImported ? 'Supplier import' : 'Unknown call-off';
  const emptyStateText = isImported
    ? 'No imported delivery lines available. Use Import & Allocate from the ASN tab to bring them in.'
    : 'No available delivery lines found. Delivery lines are created when a call-off is confirmed.';
  const [expandedCallOffs, setExpandedCallOffs] = useState<Set<string>>(new Set());

  const groupedDeliveryLines = useMemo(() => {
    const groups = new Map<string, DeliveryLineOption[]>();
    for (const dl of deliveryLines) {
      // CR-6038: imported lines have no call-off, so grouping them by
      // call_off_reference collapses everything into one "Supplier import"
      // bucket. Group imported lines by their delivery number instead so
      // each delivery is listed separately.
      const ref = isImported
        ? (dl.delivery_number || emptyGroupLabel)
        : (dl.call_off_reference || emptyGroupLabel);
      if (!groups.has(ref)) groups.set(ref, []);
      groups.get(ref)!.push(dl);
    }
    // CR-7744: within a call-off, the lines the customer flagged come first.
    return Array.from(groups.entries()).map(
      ([ref, lines]) => [ref, priorityFirst(lines, l => Boolean(l.is_priority))] as const,
    );
  }, [deliveryLines, emptyGroupLabel, isImported]);

  useEffect(() => {
    setExpandedCallOffs(new Set());
  }, [deliveryLines]);

  // One ASN carries one call-off: the notice is dispatched, settled and
  // completed as that call-off, so a second call-off's lines on it were never
  // dispatched or completed. Once a line is ticked, other call-offs are locked
  // (the backend refuses a mix too). Imported lines have no call-off.
  const lockedCallOff = useMemo(() => {
    if (isImported) return null;
    const picked = deliveryLines.find((dl) => formData.delivery_line_ids.includes(dl.id));
    return picked ? picked.call_off ?? null : null;
  }, [deliveryLines, formData.delivery_line_ids, isImported]);
  const lockedTitle = 'An ASN carries one call-off — raise a separate ASN for this one';

  const toggleCallOffExpand = (ref: string) => {
    setExpandedCallOffs((prev) => {
      const next = new Set(prev);
      if (next.has(ref)) next.delete(ref);
      else next.add(ref);
      return next;
    });
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium mb-2">ASN Number</label>
        <div className="w-full px-3 py-2 border rounded-lg bg-muted/40 text-sm text-muted-foreground">
          Auto-generated on save (e.g. ASN000001)
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">Source</label>
        <div className="flex rounded-lg border overflow-hidden">
          <button
            type="button"
            onClick={() => onSourceChange('schedules')}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium transition-colors ${
              sourceType === 'schedules'
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            <FileText className="h-4 w-4" />
            PO Line Schedules
          </button>
          <button
            type="button"
            onClick={() => onDeliveryLineSourceChange?.('CallOff')}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium transition-colors border-l ${
              sourceType === 'delivery_lines' && !isImported
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            <Truck className="h-4 w-4" />
            Call-Off Lines
          </button>
          <button
            type="button"
            onClick={() => onDeliveryLineSourceChange?.('SupplierImport')}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium transition-colors border-l ${
              sourceType === 'delivery_lines' && isImported
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            <Boxes className="h-4 w-4" />
            Imported Lines
          </button>
          <button
            type="button"
            onClick={onOpenExcelImport}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium transition-colors bg-white text-gray-700 hover:bg-gray-50 border-l"
          >
            <FileUp className="h-4 w-4" />
            Import from Excel
          </button>
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          Excel import creates new delivery lines from your ERP data and adds them to this ASN.
        </p>
      </div>

      {sourceType === 'schedules' && (
        <div>
          <label className="block text-sm font-medium mb-2">
            Select PO Line Schedules <span className="text-red-500">*</span>
          </label>
          {loadingSchedules ? (
            <div className="text-sm text-muted-foreground">Loading schedules...</div>
          ) : schedules.length === 0 ? (
            <div className="text-sm text-muted-foreground">No available schedules found.</div>
          ) : (
            <div className="border rounded-lg max-h-48 overflow-y-auto">
              {schedules.map((schedule) => (
                <label
                  key={schedule.id}
                  className="flex items-center gap-3 p-3 hover:bg-muted/50 cursor-pointer border-b last:border-b-0"
                >
                  <input
                    type="checkbox"
                    checked={formData.schedules.includes(schedule.id)}
                    onChange={() => onToggleSchedule(schedule.id)}
                    className="w-4 h-4"
                  />
                  {/* The schedules list returns flat fields; the nested
                      po_line_details shape is read only as a fallback. Reading
                      it alone left every row blank ("- Line", "Part:"). */}
                  <div className="flex-1 text-sm">
                    <div className="font-medium">
                      {schedule.po_number ?? schedule.po_line_details?.po_details?.po_number} - Line{' '}
                      {schedule.line_number ?? schedule.po_line_details?.line_number}
                    </div>
                    <div className="text-muted-foreground">
                      Part: {schedule.part_number ?? schedule.po_line_details?.part_number} | Qty:{' '}
                      {schedule.schedule_qty ?? schedule.quantity} | Due:{' '}
                      {(schedule.schedule_date ?? schedule.due_date)
                        ? formatDate((schedule.schedule_date ?? schedule.due_date) as string)
                        : 'N/A'}
                    </div>
                  </div>
                </label>
              ))}
            </div>
          )}
        </div>
      )}

      {sourceType === 'delivery_lines' && (
        <div>
          <label className="block text-sm font-medium mb-2">
            Select Delivery Lines <span className="text-red-500">*</span>
          </label>
          {lockedCallOff !== null && (
            <p className="text-xs text-muted-foreground mb-2">
              An ASN carries one call-off. Lines from other call-offs are locked — raise a separate ASN for each.
            </p>
          )}
          {loadingDeliveryLines ? (
            <div className="text-sm text-muted-foreground">Loading delivery lines...</div>
          ) : deliveryLines.length === 0 ? (
            <div className="text-sm text-muted-foreground">{emptyStateText}</div>
          ) : (
            <div className="border rounded-lg max-h-80 overflow-y-auto">
              {groupedDeliveryLines.map(([callOffRef, lines]) => {
                const isExpanded = expandedCallOffs.has(callOffRef);
                const lineIds = lines.map((l) => l.id);
                const selectedCount = lineIds.filter((id) =>
                  formData.delivery_line_ids.includes(id)
                ).length;
                const priorityCount = lines.filter((l) => l.is_priority).length;
                const allSelected = selectedCount === lineIds.length && lineIds.length > 0;
                const someSelected = selectedCount > 0 && !allSelected;
                const locked = lockedCallOff !== null && (lines[0]?.call_off ?? null) !== lockedCallOff;
                return (
                  <div key={callOffRef} className={`border-b last:border-b-0 ${locked ? 'opacity-50' : ''}`}>
                    <div className="flex items-center gap-2 p-3 bg-muted/30 hover:bg-muted/50">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        ref={(el) => {
                          if (el) el.indeterminate = someSelected;
                        }}
                        onChange={() => !locked && onSelectAllInGroup(lines)}
                        onClick={(e) => e.stopPropagation()}
                        disabled={locked}
                        title={locked ? lockedTitle : undefined}
                        className={`w-4 h-4 ${locked ? 'cursor-not-allowed' : ''}`}
                      />
                      <button
                        type="button"
                        onClick={() => toggleCallOffExpand(callOffRef)}
                        className="flex items-center gap-2 flex-1 text-left"
                      >
                        {isExpanded ? (
                          <ChevronDown className="h-4 w-4 text-muted-foreground" />
                        ) : (
                          <ChevronRight className="h-4 w-4 text-muted-foreground" />
                        )}
                        <span className="font-medium text-sm">{callOffRef}</span>
                        {priorityCount > 0 && (
                          <PriorityBadge
                            title={`${priorityCount} priority ${priorityCount === 1 ? 'line' : 'lines'} — pick first`}
                          />
                        )}
                        <span className="text-xs text-muted-foreground ml-auto">
                          {selectedCount > 0 && (
                            <span className="text-blue-600 font-medium">{selectedCount} / </span>
                          )}
                          {lines.length} {lines.length === 1 ? 'line' : 'lines'}
                        </span>
                      </button>
                    </div>
                    {isExpanded &&
                      lines.map((dl) => (
                        <label
                          key={dl.id}
                          title={locked ? lockedTitle : undefined}
                          className={`flex items-center gap-3 p-2 pl-10 hover:bg-muted/30 border-t ${
                            locked ? 'cursor-not-allowed' : 'cursor-pointer'
                          } ${dl.is_priority ? PRIORITY_EDGE_CLASS : ''}`}
                        >
                          <input
                            type="checkbox"
                            checked={formData.delivery_line_ids.includes(dl.id)}
                            onChange={() => !locked && onToggleDeliveryLine(dl.id)}
                            disabled={locked}
                            className="w-4 h-4"
                          />
                          <div className="flex-1 text-xs">
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{dl.delivery_number}</span>
                              {/* CR-7142: Warehouse vs Direct, stated outright.
                                  It used to be a bare " | Direct" at the end of
                                  the detail line, so the only reliable clue was
                                  spotting "direct" inside a code like DL007 —
                                  and ticking a direct line onto a warehouse
                                  load is exactly the mistake that causes. */}
                              <span
                                className={
                                  dl.delivery_option === 'direct'
                                    ? 'px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-purple-100 text-purple-700'
                                    : 'px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-sky-100 text-sky-700'
                                }
                              >
                                {dl.delivery_option === 'direct' ? 'Direct' : 'Warehouse'}
                              </span>
                              {dl.is_priority && <PriorityBadge title="The customer wants this line picked first" />}
                            </div>
                            <div className="text-muted-foreground">
                              Part: {dl.part_number} | Qty: {dl.qty_released}
                              {dl.po_order_number && (
                                <> | PO: {dl.po_order_number}{dl.po_line_number != null && <> Line {dl.po_line_number}</>}</>
                              )}
                              {dl.sales_order && <> | SO: {dl.sales_order}</>}
                            </div>
                          </div>
                        </label>
                      ))}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
